import { useEffect } from 'react';
import { cn } from './cn';
import { R, T, S, Z } from './tokens';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export function Modal({
  open,
  onClose,
  children,
  className,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  // Backdrop click does NOT dismiss (aligns with red_coral constitution): on
  // touch a stray tap / synthetic mouse event on the overlay would close the
  // modal and silently discard in-progress create/edit work. Closing is
  // explicit only — the X / cancel button in children, or Escape (desktop).
  return (
    <div
      className={cn(
        'fixed inset-0 flex items-end md:items-center justify-center md:p-4 bg-black/50 backdrop-blur-sm',
        Z.modal,
      )}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'bg-white w-full overflow-y-auto max-h-[85vh] pb-safe md:pb-0',
          R.modal,
          S.lg,
          T.normal,
          'max-w-none md:max-w-sm',
          className,
        )}
      >
        <div
          aria-hidden="true"
          className="md:hidden w-12 h-1 rounded-full bg-slate-300 mx-auto mt-2 mb-1"
        />
        {children}
      </div>
    </div>
  );
}
